import React from "react";
import { Card, Empty, Tag, Tooltip as AntTooltip } from "antd";
import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { ChartState, MetricLabel } from "./ChartParts";
import DeltaBadge from "./DeltaBadge";
import { COMPARE_COLORS, LEGEND_STYLE, fmtNumber, yLabel } from "../gaFormat";

const C = COMPARE_COLORS;

// Marks blocks added for the leadership review so they are easy to spot while deciding what to keep.
function CardTitle({ title, hint }) {
  return (
    <span className="inline-flex items-center">
      {hint ? <MetricLabel title={title} hint={hint} /> : title}
      {/* <Tag color="blue" className="!ml-2 !mr-0 !text-[10px] !leading-[16px]">new</Tag> */}
    </span>
  );
}

// order "alpha" (default): one per line, alphabetical. order "rank": kept in the order the
// rows were already given (e.g. biggest-first), numbered — for a top-N list. columnLabel
// captions what the right-hand number means, since it isn't always the same metric.
function TenantListTooltip({ rows, valueKey, order = "alpha", columnLabel }) {
  const sorted = order === "rank" ? rows : [...rows].sort((a, b) => a.client.localeCompare(b.client));
  return (
    <div className="max-h-[260px] overflow-y-auto py-0.5">
      {columnLabel && (
        <div className="flex justify-between gap-3 whitespace-nowrap text-slate-400 pb-1 mb-1 border-0 border-b border-solid border-slate-600">
          <span>Tenant</span>
          <span>{columnLabel}</span>
        </div>
      )}
      {sorted.map((r, i) => (
        <div key={r.client} className="flex justify-between gap-3 whitespace-nowrap">
          <span>{order === "rank" ? `${i + 1}. ` : ""}{r.client}</span>
          <span className="text-slate-300">{fmtNumber(r[valueKey])}</span>
        </div>
      ))}
    </div>
  );
}

function Stat({ label, hint, value, badge, sub, tenantRows, tenantValueKey, tenantOrder, tenantColumnLabel }) {
  const valueEl = <div className="text-[18px] font-semibold text-slate-900 leading-[1.3]">{value}</div>;
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <div className="text-[11px] text-slate-500">{hint ? <MetricLabel title={label} hint={hint} /> : label}</div>
      {tenantRows?.length > 0 ? (
        <AntTooltip
          title={<TenantListTooltip rows={tenantRows} valueKey={tenantValueKey} order={tenantOrder} columnLabel={tenantColumnLabel} />}
          placement="bottomLeft"
        >
          <div className="w-fit cursor-help underline decoration-dotted decoration-slate-500 underline-offset-4">{valueEl}</div>
        </AntTooltip>
      ) : (
        valueEl
      )}
      <div className="flex flex-wrap items-center gap-x-1.5">
        {badge}
        {sub && <span className="text-[11px] text-slate-400 whitespace-nowrap">{sub}</span>}
      </div>
    </div>
  );
}

const pct = (v) => (v == null ? "--" : `${(v * 100).toFixed(1)}%`);
const ratio = (a, b) => (b > 0 ? a / b : null);
const fixed = (v, digits = 1) => (v == null ? "--" : v.toFixed(digits));

export function TenantAdoptionCard({ state, onRetry, adoption }) {
  return (
    <Card
      size="small"
      className="flex-1"
      title={<CardTitle title="Tenant adoption" hint="Tenants with at least one active user in each period. Top-5 share = share of all tenant active users that comes from the 5 biggest tenants — higher means usage is more concentrated." />}
    >
      <ChartState state={state} skeleton="table" height={120} onRetry={onRetry}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Stat
            label="Active tenants"
            hint="Number of tenants with at least one active user in this period (activeUsers > 0)."
            value={adoption.activeNow}
            badge={<DeltaBadge current={adoption.activeNow} previous={adoption.activeBefore} />}
            sub={`prev ${adoption.activeBefore}`}
          />
          <Stat
            label="Started using"
            hint="Tenants with active users in this period that had zero active users in the previous period."
            value={adoption.started.length}
            sub="active now, none before"
            tenantRows={adoption.started}
            tenantValueKey="now"
            tenantColumnLabel="Active users now"
          />
          <Stat
            label="Went quiet"
            hint="Tenants with active users in the previous period that have zero active users in this period. May also mean tracking stopped for that tenant rather than real churn."
            value={adoption.stopped.length}
            sub="active before, none now"
            tenantRows={adoption.stopped}
            tenantValueKey="before"
            tenantColumnLabel="Active users before"
          />
          <Stat
            label="Top-5 share"
            hint="Active users from the 5 largest tenants ÷ active users across all tenants, this period. Higher means usage is more concentrated in a few tenants."
            value={pct(adoption.topShareNow)}
            badge={<DeltaBadge current={adoption.topShareNow} previous={adoption.topShareBefore} mode="points" invert />}
            sub={`prev ${pct(adoption.topShareBefore)}`}
            tenantRows={adoption.topTenantsNow}
            tenantValueKey="now"
            tenantOrder="rank"
            tenantColumnLabel="Active users now"
          />
        </div>
      </ChartState>
    </Card>
  );
}

export function UsageDepthCard({ state, onRetry, overview }) {
  const cur = overview?.current || {};
  const prev = overview?.previous || {};
  const metrics = [
    { label: "Sessions per user", hint: "Sessions ÷ active users. How often an active user comes back in the period.", f: (o) => ratio(o.sessions, o.activeUsers) },
    { label: "Views per session", hint: "Views ÷ sessions. How much gets done per visit.", f: (o) => ratio(o.screenPageViews, o.sessions) },
    { label: "Views per user", hint: "Views ÷ active users.", f: (o) => ratio(o.screenPageViews, o.activeUsers), digits: 0 },
    { label: "New users share", hint: "New users ÷ active users. Higher = more of the activity comes from first-time users.", f: (o) => ratio(o.newUsers, o.activeUsers), isPct: true },
  ];
  return (
    <Card size="small" className="flex-1" title={<CardTitle title="Usage depth" />}>
      <ChartState state={state} skeleton="table" height={120} onRetry={onRetry}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {metrics.map((m) => {
            const now = m.f(cur);
            const before = m.f(prev);
            const show = (v) => (m.isPct ? pct(v) : fixed(v, m.digits ?? 1));
            return (
              <Stat
                key={m.label}
                label={m.label}
                hint={m.hint}
                value={show(now)}
                badge={<DeltaBadge current={now} previous={before} mode={m.isPct ? "points" : "pct"} />}
                sub={`prev ${show(before)}`}
              />
            );
          })}
        </div>
      </ChartState>
    </Card>
  );
}

export function WeekdayCard({ state, onRetry, current, previous, labels }) {
  const data = current.map((d, i) => ({
    weekday: d.weekday,
    current: d.value == null ? null : Math.round(d.value),
    previous: previous[i]?.value == null ? null : Math.round(previous[i].value),
  }));
  return (
    <Card
      size="small"
      className="flex-1"
      title={<CardTitle title="Average daily active users by weekday" hint="Each bar is the average of that weekday's daily active users across the period, so Mondays are compared with Mondays regardless of where the ranges start." />}
    >
      <ChartState state={state} skeleton="bar" onRetry={onRetry} empty={data.length === 0}>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data} margin={{ top: 16, right: 16, bottom: 8, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="weekday" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} label={yLabel("Avg active users")} />
            <Tooltip formatter={(v) => Number(v).toLocaleString()} />
            <Legend wrapperStyle={LEGEND_STYLE} />
            <Bar dataKey="current" name={labels.current} fill={C.current} radius={[4, 4, 0, 0]} />
            <Bar dataKey="previous" name={labels.previous} fill={C.previousBar} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartState>
    </Card>
  );
}

export function WebAppShareCard({ state, onRetry, current, previous, labels }) {
  const row = (label, split) => ({
    period: label,
    web: split.total ? split.web / split.total : 0,
    app: split.total ? split.app / split.total : 0,
    webSessions: split.web,
    appSessions: split.app,
  });
  const data = [row(labels.current, current), row(labels.previous, previous)];
  const pctLabel = (v) => (v >= 0.06 ? `${(v * 100).toFixed(0)}%` : "");
  return (
    <Card
      size="small"
      className="flex-1"
      title={<CardTitle title="Web vs App share of sessions" hint="Share of sessions from the web app (desktop, tablet, mobile browser) vs the iOS and Android apps." />}
    >
      <ChartState state={state} skeleton="bar" onRetry={onRetry} empty={!current.total && !previous.total}>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={data} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
            <XAxis type="number" domain={[0, 1]} tickFormatter={(v) => `${Math.round(v * 100)}%`} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="period" tick={{ fontSize: 11 }} width={170} />
            <Tooltip
              formatter={(v, name, item) => {
                const sessions = name === "Web" ? item.payload.webSessions : item.payload.appSessions;
                return `${(v * 100).toFixed(1)}% (${fmtNumber(sessions)} sessions)`;
              }}
            />
            <Legend wrapperStyle={LEGEND_STYLE} />
            <Bar dataKey="web" name="Web" stackId="s" fill={C.current}>
              <LabelList dataKey="web" position="center" formatter={pctLabel} style={{ fontSize: 11, fill: "#fff" }} />
            </Bar>
            <Bar dataKey="app" name="App" stackId="s" fill={C.app}>
              <LabelList dataKey="app" position="center" formatter={pctLabel} style={{ fontSize: 11, fill: "#fff" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className="text-[12px] text-slate-500 mt-1">
          App sessions {fmtNumber(current.app)} vs {fmtNumber(previous.app)}{" "}
          <DeltaBadge current={current.app} previous={previous.app} />
        </div>
      </ChartState>
    </Card>
  );
}

export function NewReturningCard({ state, onRetry, overview, labels }) {
  const split = (o = {}) => {
    const active = Number(o.activeUsers) || 0;
    const newUsers = Math.min(Number(o.newUsers) || 0, active);
    return { newUsers, returning: active - newUsers };
  };
  const data = [
    { period: labels.current, ...split(overview?.current) },
    { period: labels.previous, ...split(overview?.previous) },
  ];
  return (
    <Card
      size="small"
      className="flex-1"
      title={<CardTitle title="New vs returning users" hint="Active users split into first-time users and users who had used SafetyQube before the period. Returning = active − new." />}
    >
      <ChartState state={state} skeleton="bar" onRetry={onRetry}>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data} margin={{ top: 16, right: 16, bottom: 8, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} label={yLabel("Active users")} />
            <Tooltip formatter={(v) => Number(v).toLocaleString()} />
            <Legend wrapperStyle={LEGEND_STYLE} />
            <Bar dataKey="returning" name="Returning" stackId="u" fill={C.current}>
              <LabelList dataKey="returning" position="center" formatter={fmtNumber} style={{ fontSize: 11, fill: "#fff" }} />
            </Bar>
            <Bar dataKey="newUsers" name="New" stackId="u" fill={C.up} radius={[4, 4, 0, 0]}>
              <LabelList dataKey="newUsers" position="center" formatter={fmtNumber} style={{ fontSize: 11, fill: "#fff" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartState>
    </Card>
  );
}

export function ModuleMoversCard({ state, onRetry, movers }) {
  const data = [...movers.ups, ...[...movers.downs].reverse()];
  return (
    <Card
      size="small"
      className="flex-1"
      title={<CardTitle title="Fastest growing / declining modules" hint="Change in views per module between the two periods: the biggest increases and the biggest decreases." />}
    >
      <ChartState state={state} skeleton="bar" onRetry={onRetry} empty={data.length === 0}>
        {data.length === 0 ? (
          <Empty description="No change between periods" />
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(160, data.length * 24 + 40)}>
            <BarChart data={data} layout="vertical" margin={{ top: 8, right: 48, bottom: 8, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="title" tick={{ fontSize: 10 }} width={170} />
              <Tooltip formatter={(v, _n, item) => [`${v > 0 ? "+" : ""}${Number(v).toLocaleString()} (${fmtNumber(item.payload.before)} → ${fmtNumber(item.payload.now)})`, "Views"]} />
              <Bar dataKey="change" radius={[0, 4, 4, 0]}>
                {data.map((r) => (
                  <Cell key={r.title} fill={r.change > 0 ? C.up : C.down} />
                ))}
                <LabelList dataKey="change" position="right" style={{ fontSize: 10, fill: "#475569" }} formatter={(v) => (v > 0 ? `+${fmtNumber(v)}` : fmtNumber(v))} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartState>
    </Card>
  );
}
